/*
 * <pdp-ship-notify>: the product page's "Email me once it ships" link and its pop-up.
 * The form sends the shopper's name, email and phone to Klaviyo's client events API as one event (named by
 * the block) on their profile, carrying the product; a Klaviyo flow on that event sends the email.
 * When the block names a list (the snippet passes one only with its consent small print shown), the email is
 * then subscribed to it for marketing through Klaviyo's client subscriptions API; the phone stays on the
 * profile, never an SMS consent. The confirmation screen replaces the form once Klaviyo has accepted the
 * event; a failed list subscription is logged and does not undo the shipping notice. When Klaviyo is not on
 * the page or cannot be reached (an ad blocker, declined tracking) the shopper sees an error instead of a
 * signup that went nowhere.
 *
 * Both calls are plain fetches, not klaviyo.identify() and klaviyo.track(): on the live site those promises
 * never settled (2026-10-21), and the loaded window.klaviyo is a proxy whose every property is a function,
 * "then" included, so even awaiting the object itself hangs. A fetch answers with a status. Nothing on the
 * object is awaited without a timeout; identify() is still called, unawaited, so the browser is tied to the
 * profile for Klaviyo's onsite tracking.
 */
(() => {
  if (window.customElements.get("pdp-ship-notify")) return;

  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Klaviyo retires a revision two years after its date. 2024-10-15 had the profile's "subscriptions" consent
  // only in beta and answered the list call 400 (2026-10-21); 2025-01-15 and later take it.
  const KLAVIYO_REVISION = "2026-07-15";
  const KLAVIYO_TIMEOUT_MS = 8000;
  const KLAVIYO_WAIT_MS = 5000;
  const ACCOUNT_WAIT_MS = 1500;

  // E.164 for Klaviyo. A number with + keeps its country code. Without one, a 10 digit number (or 11
  // starting with 1) is North American only on a +1 store (US, CA); every other store needs the +.
  function toE164(raw, northAmerican) {
    const value = String(raw || "").trim();
    if (!value) return "";
    const digits = value.replace(/\D/g, "");
    if (value.startsWith("+"))
      return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
    if (!northAmerican) return null;
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    return null;
  }

  function splitName(raw) {
    const parts = String(raw || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    return { first: parts.shift() || "", last: parts.join(" ") };
  }

  // Whether klaviyo.js loaded (identify and track are functions), after waiting for it. A boolean, never
  // the object (see the header).
  function klaviyoLoaded(timeout = KLAVIYO_WAIT_MS) {
    const ready = () =>
      typeof window.klaviyo?.identify === "function" &&
      typeof window.klaviyo?.track === "function";
    if (ready()) return Promise.resolve(true);
    return new Promise((resolve) => {
      const started = Date.now();
      const timer = setInterval(() => {
        if (ready() || Date.now() - started >= timeout) {
          clearInterval(timer);
          resolve(ready());
        }
      }, 200);
    });
  }

  // The store's public Klaviyo site id: from the loaded Klaviyo object, else the klaviyo.js tag the
  // Klaviyo app embed adds. Never resolve a promise with the object itself (see the header).
  async function klaviyoCompanyId() {
    const klaviyo = window.klaviyo;
    if (typeof klaviyo?.account === "function") {
      const id = await Promise.race([
        Promise.resolve()
          .then(() => klaviyo.account())
          .catch(() => ""),
        new Promise((resolve) => setTimeout(resolve, ACCOUNT_WAIT_MS, "")),
      ]);
      if (typeof id === "string" && id) return id;
    }
    for (const script of document.querySelectorAll(
      'script[src*="klaviyo.com/onsite/js/"]',
    )) {
      const id = new URL(script.src, location.href).searchParams.get(
        "company_id",
      );
      if (id) return id;
    }
    return "";
  }

  // A POST to one of Klaviyo's client APIs, which answer 202 with no body.
  async function klaviyoPost(path, companyId, data) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), KLAVIYO_TIMEOUT_MS);
    const response = await fetch(
      `https://a.klaviyo.com/client/${path}?company_id=${encodeURIComponent(companyId)}`,
      {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/vnd.api+json",
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({ data }),
      },
    ).finally(() => clearTimeout(timer));
    if (!response.ok)
      throw Object.assign(
        new Error(`Klaviyo ${path} failed: ${response.status}`),
        { status: response.status },
      );
  }

  // The shipping notice: the event, with the profile it belongs to.
  function sendEvent(companyId, name, profile, properties) {
    return klaviyoPost("events", companyId, {
      type: "event",
      attributes: {
        properties,
        metric: { data: { type: "metric", attributes: { name } } },
        profile: { data: { type: "profile", attributes: profile } },
      },
    });
  }

  // Email marketing consent on the list.
  function subscribeToList(companyId, listId, email, source) {
    return klaviyoPost("subscriptions", companyId, {
      type: "subscription",
      attributes: {
        custom_source: source,
        profile: {
          data: {
            type: "profile",
            attributes: {
              email,
              subscriptions: {
                email: { marketing: { consent: "SUBSCRIBED" } },
              },
            },
          },
        },
      },
      relationships: { list: { data: { type: "list", id: listId } } },
    });
  }

  class PdpShipNotify extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.dialog = this.querySelector("[data-ship-notify-dialog]");
      this.form = this.querySelector("[data-ship-notify-form]");
      this.error = this.querySelector("[data-ship-notify-error]");
      this.success = this.querySelector("[data-ship-notify-success]");
      this.intro = this.querySelector("[data-ship-notify-intro]");
      this.done = this.querySelector("[data-ship-notify-done]");
      if (!this.dialog || !this.form) return;
      this.bound = true;

      this.querySelector("[data-ship-notify-open]")?.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          this.open();
        },
      );
      this.querySelectorAll("[data-ship-notify-close]").forEach((el) =>
        el.addEventListener("click", () => this.close()),
      );
      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        this.submit();
      });
    }

    open() {
      this.showError("");
      if (typeof this.dialog.showModal === "function") this.dialog.showModal();
      else this.dialog.setAttribute("open", "");
      // After a signup the pop-up reopens on the confirmation screen.
      if (this.form.hidden) (this.done || this.success)?.focus();
      else this.form.querySelector("input")?.focus();
    }

    close() {
      if (typeof this.dialog.close === "function") this.dialog.close();
      else this.dialog.removeAttribute("open");
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    async submit() {
      if (this.sending) return;
      const field = (name) => this.form.elements.namedItem(name)?.value || "";
      const email = field("email").trim();
      const name = splitName(field("name"));
      const phone = toE164(field("phone"), this.dataset.phoneCountry === "1");

      if (!name.first) return this.showError("Please enter your name.");
      if (!EMAIL.test(email))
        return this.showError("Please enter a valid email.");
      if (phone === null)
        return this.showError(
          this.dataset.phoneCountry === "1"
            ? "Please enter a valid phone number."
            : "Please enter your phone number with its country code, starting with +.",
        );

      const listId = (this.dataset.listId || "").trim();
      const profile = { email, first_name: name.first };
      if (name.last) profile.last_name = name.last;

      this.sending = true;
      this.showError("");
      const submit = this.form.querySelector('[type="submit"]');
      if (submit) submit.disabled = true;
      try {
        if (!(await klaviyoLoaded()))
          throw new Error("Klaviyo is not on the page");
        const companyId = await klaviyoCompanyId();
        if (!companyId) throw new Error("No Klaviyo site id on the page");
        const event = this.dataset.event || "Notify Me When It Ships";
        const properties = {
          ProductName: this.dataset.productName || "",
          ProductHandle: this.dataset.productHandle || "",
          ProductURL: this.dataset.productUrl || "",
          Store: this.dataset.store || "",
          ShippingLine: this.dataset.shippingLine || "",
          ...(listId && { List: listId }),
        };
        // Klaviyo refuses the whole event over a phone it cannot use; the phone is optional, so the notice
        // goes again without it.
        await sendEvent(
          companyId,
          event,
          phone ? { ...profile, phone_number: phone } : profile,
          properties,
        ).catch((error) => {
          if (!phone || error.status !== 400) throw error;
          return sendEvent(companyId, event, profile, properties);
        });
        try {
          const identified = window.klaviyo.identify({
            $email: email,
            $first_name: name.first,
            ...(name.last && { $last_name: name.last }),
          });
          identified?.catch?.(() => {});
        } catch {}
        if (listId)
          await subscribeToList(
            companyId,
            listId,
            email,
            `Ship notify: ${this.dataset.productName || ""}`.trim(),
          ).catch((error) =>
            console.error("Ship notify list subscription failed", error),
          );
        // The second screen: the heading, text and form give way to the confirmation.
        this.form.hidden = true;
        if (this.intro) this.intro.hidden = true;
        if (this.success) this.success.hidden = false;
        if (this.done) {
          this.dialog.setAttribute("aria-labelledby", this.done.id);
          this.done.focus();
        }
      } catch (error) {
        this.showError(
          "Sorry, we couldn't sign you up. Please try again, or turn off your ad blocker for this site.",
        );
      } finally {
        this.sending = false;
        if (submit) submit.disabled = false;
      }
    }
  }

  window.customElements.define("pdp-ship-notify", PdpShipNotify);
})();
